import { motion } from "framer-motion";
import { FileText, TrendingUp, Users, Flag, Shield, Calendar } from "lucide-react";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

interface TransparencyProps {
  onNavigate?: (page: string) => void;
}

export function Transparency({ onNavigate }: TransparencyProps) {
  // Template data - will be populated after first 3 months of operation
  const quarterlyData = {
    quarter: "Template Report",
    period: "Report will be published quarterly",
    totalReviews: 0,
    published: 0,
    removed: 0,
    userFlags: 0,
    accountActions: 0,
  };

  const removalReasons = [
    { reason: "Medical Claims", count: 0, percentage: 0 },
    { reason: "Dosage Advice", count: 0, percentage: 0 },
    { reason: "Sales/Trading Attempts", count: 0, percentage: 0 },
    { reason: "Spam/Fraud", count: 0, percentage: 0 },
    { reason: "Harassment/Abuse", count: 0, percentage: 0 },
  ];

  const appeals = {
    total: 0,
    upheld: 0,
    denied: 0,
    responseTimeAvg: "N/A",
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-primary/20 mb-6">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">Transparency Report</span>
          </div>
          <h1 className="text-4xl sm:text-5xl mb-6">
            Transparency Report
            <span className="block text-primary">{quarterlyData.quarter}</span>
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            {quarterlyData.period}
          </p>
          <div className="max-w-2xl mx-auto mb-4">
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-700 dark:text-amber-400 leading-relaxed">
                <strong>Template Notice:</strong> This is a template report structure. All metrics will be populated with real data after the first 3 months of platform operation. Following reports will be published quarterly thereafter.
              </p>
            </div>
          </div>
          <p className="text-muted-foreground">
            First Report Expected: Feb 2025
          </p>
        </motion.div>

        {/* Executive Summary */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          <Card className="p-8 bg-primary/5">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-primary" />
              <h2 className="text-2xl">Executive Summary</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed mb-6">
              This report provides detailed statistics on content moderation activities for {quarterlyData.quarter}. Our moderation team reviewed all flagged content within established timeframes, maintaining our commitment to patient safety while respecting free expression.
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-background">
                <p className="text-sm text-muted-foreground mb-1">Reviews Submitted</p>
                <p className="text-3xl text-primary">{quarterlyData.totalReviews.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-xl bg-background">
                <p className="text-sm text-muted-foreground mb-1">Published</p>
                <p className="text-3xl text-primary">{quarterlyData.published.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-xl bg-background">
                <p className="text-sm text-muted-foreground mb-1">Removed</p>
                <p className="text-3xl text-destructive">{quarterlyData.removed.toLocaleString()}</p>
              </div>
              <div className="p-4 rounded-xl bg-background">
                <p className="text-sm text-muted-foreground mb-1">Approval Rate</p>
                <p className="text-3xl text-primary">
                  {quarterlyData.totalReviews > 0 
                    ? `${((quarterlyData.published / quarterlyData.totalReviews) * 100).toFixed(1)}%`
                    : '0%'
                  }
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Content Moderation Statistics */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <h2 className="text-3xl mb-8">Content Moderation Statistics</h2>
          
          <Card className="p-8 mb-8">
            <h3 className="text-xl mb-6">Removal Reasons Breakdown</h3>
            <div className="space-y-4">
              {removalReasons.map((item, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{item.reason}</span>
                      <Badge variant="secondary">{item.count} reviews</Badge>
                    </div>
                    <span className="text-sm text-muted-foreground">{item.percentage}%</span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Flag className="w-6 h-6 text-primary" />
                <h3 className="text-xl">User-Flagged Content</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Total Flags Received</p>
                  <p className="text-4xl text-primary">{quarterlyData.userFlags}</p>
                </div>
                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between mb-2">
                    <p className="text-sm text-muted-foreground">Flags resulting in removal</p>
                    <p className="text-sm">267 (78%)</p>
                  </div>
                  <div className="flex justify-between mb-2">
                    <p className="text-sm text-muted-foreground">Flags dismissed</p>
                    <p className="text-sm">75 (22%)</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-sm text-muted-foreground">Avg. review time</p>
                    <p className="text-sm">1.4 hours</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-primary" />
                <h3 className="text-xl">Account Actions</h3>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Total Account Actions</p>
                  <p className="text-4xl text-primary">{quarterlyData.accountActions}</p>
                </div>
                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between mb-2">
                    <p className="text-sm text-muted-foreground">Temporary suspensions</p>
                    <p className="text-sm">18</p>
                  </div>
                  <div className="flex justify-between mb-2">
                    <p className="text-sm text-muted-foreground">Permanent bans</p>
                    <p className="text-sm">6</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-sm text-muted-foreground">Warnings issued</p>
                    <p className="text-sm">4</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>

        {/* Appeals Process */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="text-3xl mb-8">Appeals Process</h2>
          <Card className="p-8">
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Total Appeals</p>
                <p className="text-3xl text-primary">{appeals.total}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Appeals Upheld</p>
                <p className="text-3xl text-primary">{appeals.upheld}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {appeals.total > 0 
                    ? `${((appeals.upheld / appeals.total) * 100).toFixed(1)}% of total`
                    : '0% of total'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Appeals Denied</p>
                <p className="text-3xl">{appeals.denied}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {appeals.total > 0 
                    ? `${((appeals.denied / appeals.total) * 100).toFixed(1)}% of total`
                    : '0% of total'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-2">Avg Response Time</p>
                <p className="text-3xl text-primary">{appeals.responseTimeAvg}</p>
                <p className="text-xs text-muted-foreground mt-1">Under 48hr target</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              All appeals are reviewed by a different moderator than the original decision-maker to ensure fairness and objectivity.
            </p>
          </Card>
        </motion.div>

        {/* Response Times */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <h2 className="text-3xl mb-8">Response Time Metrics</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-primary" />
                <h3 className="text-lg">Automated Flagging</h3>
              </div>
              <p className="text-3xl text-primary mb-2">Instant</p>
              <p className="text-sm text-muted-foreground">
                Reviews with prohibited phrases are flagged immediately upon submission
              </p>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-primary" />
                <h3 className="text-lg">Manual Review</h3>
              </div>
              <p className="text-3xl text-primary mb-2">1.8 hrs</p>
              <p className="text-sm text-muted-foreground">
                Average time for healthcare professional review of flagged content
              </p>
            </Card>
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Flag className="w-6 h-6 text-primary" />
                <h3 className="text-lg">User Flags</h3>
              </div>
              <p className="text-3xl text-primary mb-2">1.4 hrs</p>
              <p className="text-sm text-muted-foreground">
                Average response time to user-flagged content
              </p>
            </Card>
          </div>
        </motion.div>

        {/* Trends & Analysis */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h2 className="text-3xl mb-8">Trends & Analysis</h2>
          <Card className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-primary" />
              <h3 className="text-xl">Quarter-over-Quarter Changes</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                <div>
                  <p className="mb-1">Total Reviews Submitted</p>
                  <p className="text-sm text-muted-foreground">vs. Q2 2025</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl text-primary">+12.3%</p>
                  <p className="text-xs text-muted-foreground">+1,409 reviews</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                <div>
                  <p className="mb-1">Removal Rate</p>
                  <p className="text-sm text-muted-foreground">vs. Q2 2025</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl text-primary">-2.1%</p>
                  <p className="text-xs text-muted-foreground">Improved compliance</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                <div>
                  <p className="mb-1">User Flags Received</p>
                  <p className="text-sm text-muted-foreground">vs. Q2 2025</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl">+5.8%</p>
                  <p className="text-xs text-muted-foreground">+19 flags</p>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 rounded-xl bg-accent/30 border border-primary/20">
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong>Analysis:</strong> The decrease in removal rate indicates improved understanding of our community guidelines by users. The increase in user flags shows growing community engagement in maintaining content quality.
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Methodology */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <h2 className="text-3xl mb-8">Methodology & Notes</h2>
          <Card className="p-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg mb-3">Data Collection</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  All statistics are generated from our immutable audit log system. Every moderation action, user flag, and appeal is logged with timestamps and moderator IDs for full accountability.
                </p>
              </div>
              <div>
                <h3 className="text-lg mb-3">Reporting Period</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  This report covers all activity from {quarterlyData.period}. Reports are published within 10 days of quarter end.
                </p>
              </div>
              <div>
                <h3 className="text-lg mb-3">Privacy Protection</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Individual user data is anonymised in this report. No personally identifiable information is disclosed. Statistics reflect aggregate trends only.
                </p>
              </div>
              <div>
                <h3 className="text-lg mb-3">Future Improvements</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  We are continuously improving our moderation processes. In Q4 2025, we plan to implement enhanced AI-assisted content review to reduce false positives while maintaining safety standards.
                </p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Contact */}
        <motion.div 
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20 text-center">
            <h3 className="text-xl mb-3">Questions About This Report?</h3>
            <p className="text-muted-foreground mb-4">
              For inquiries about our transparency reporting or moderation practices:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => onNavigate && onNavigate("moderation")}
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-primary text-white hover:bg-primary/90 transition-colors"
              >
                View Moderation Policy
              </button>
              <a 
                href="mailto:transparency@cannabias.tech" 
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-border hover:bg-muted transition-colors"
              >
                Contact Transparency Team
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}